import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';

/**
 * Component for displaying and managing the details of a Account. Allows viewing,
 * creating, and updating Accounts.
 *
 * @component
 *
 * @example
 * <AccountDetails account={account} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.account - The Account object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The Account Details component.
 */
function AccountDetails({ account, onSubmit, onFieldChange }) {
  const { formatMessage: msg } = useIntl();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      label: '',
      host: '',
      port: '',
      username: '',
      password: '',
      type: 'API'
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetAccount = useCallback(() => {
    if (account?.id) {
      reset(account);
    } else {
      reset({
        id: '',
        label: '',
        host: '',
        port: '',
        username: '',
        password: '',
        type: 'API'
      });
    }
  }, [account, reset]);

  useEffect(() => {
    resetAccount();
  }, [account, resetAccount]);

  const clearField = (field) => {
    setValue(field, '');
  };

  const handleReset = () => {
    resetAccount();
  };

  const isCreating = !account?.id;

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
      display="flex"
      direction="column"
      sx={{ height: '100%' }}
    >
      <Stack display="flex" direction="row">
        <PersonIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
        <MDTypography variant="h5">
          {msg({
            id: isCreating
              ? 'page.accounts.details.creation.title'
              : 'page.accounts.details.modification.title'
          })}
        </MDTypography>
      </Stack>
      <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
      <Stack
        spacing={1}
        display="flex"
        direction="column"
        flex={1}
        overflow="auto"
        mb={2}
        sx={{ scrollbarWidth: 'thin' }}
      >
        {!isCreating && (
          <Controller
            name="id"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.id.label' })}
                variant="filled"
                fullWidth
                required
                size="small"
                InputProps={{
                  readOnly: true
                }}
              />
            )}
          />
        )}
        <Controller
          name="label"
          control={control}
          rules={{ required: msg({ id: 'validation.required' }) }} // Message d'erreur personnalisé
          render={({ field }) => (
            <MDInput
              required
              {...field}
              label={msg({ id: 'field.label.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.label}
              helperText={errors.label ? errors.label.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('label')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="host"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.account.host.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.host}
              helperText={errors.host ? errors.host.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('host')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="port"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.account.port.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="number"
              error={!!errors.port}
              helperText={errors.port ? errors.port.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('port')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )
              }}
            />
          )}
        />
        <Controller
          name="username"
          control={control}
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.login.label' })}
              variant="filled"
              fullWidth
              size="small"
              error={!!errors.username}
              helperText={errors.username ? errors.username.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('username')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'new-password',
                  'aria-autocomplete': 'both'
                }
              }}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          defaultValue="fake_password"
          render={({ field }) => (
            <MDInput
              {...field}
              label={msg({ id: 'field.password.label' })}
              variant="filled"
              fullWidth
              size="small"
              type="password" // Type password pour masquer le texte
              error={!!errors.password}
              helperText={errors.password ? errors.password.message : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('password')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                inputProps: {
                  autoComplete: 'new-password',
                  'aria-autocomplete': 'both'
                }
              }}
            />
          )}
        />
        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <FormControl variant="filled" fullWidth size="small" error={!!errors.type} required>
              <InputLabel>{msg({ id: 'field.account.type.label' })}</InputLabel>
              <Select
                {...field}
                label={msg({ id: 'field.account.type.label' })}
                endAdornment={
                  <InputAdornment position="end">
                    <Tooltip title={msg({ id: 'tooltip.clear' })}>
                      <IconButton onClick={() => clearField('type')}>
                        <ClearIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
              >
                <MenuItem value="EMAIL">Email</MenuItem>
                <MenuItem value="API">Api</MenuItem>
              </Select>
              {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
            </FormControl>
          )}
        />
      </Stack>
      <Divider sx={{ mt: 0 }} />
      <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
        <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
          {msg({ id: 'button.reset' })}
        </MDButton>
        <MDButton type="submit" fullWidth color="primary">
          {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
        </MDButton>
      </Stack>
    </Stack>
  );
}

AccountDetails.propTypes = {
  account: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    host: PropTypes.string,
    port: PropTypes.number,
    username: PropTypes.string,
    password: PropTypes.string,
    type: PropTypes.string
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default AccountDetails;
